import React, { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { noop } from 'ui-components/default-props/noop';

import { EditColumnsModal } from '../../components/modals/edit-columns-modal';
import {
  baseColumns,
  EMAIL_COLUMN_IDS,
  REFUND_ASSIST_COLUMNS,
  VOIDED_GRID_COLUMNS,
  VOIDED_GRID_COLUMN_IDS,
} from '../../components/shipping-grid/columns';
import { REFUND_ASSIST_VIEW_ID, VOIDED_VIEW_ID } from '../../components/shipping-grid/views/view-id-constants';
import { useShouldShowVoidType } from '../../hooks/refund-assist/use-should-show-void-type';
import { useBTEmailExperienceNew } from '../../hooks/use-seller-setting';
import { useSelectedShippingViewId } from '../../pages/shipping-page/shipping-left-nav/shipping-left-nav-context';
import Global from '../../store/global-selectors';
import { defaultColumnConfigurations } from '../../store/shipments/grid/default-column-configuration';
import { shipmentGridChangeColumns } from '../../store/shipments/grid/shipments.grid.actions';
import { saveUserSettingGridConfig } from '../../store/user/user.actions';

export const EditShipmentColumnsModalContainer = React.memo(({ onClose = noop }) => {
  const dispatch = useDispatch();
  const activeFilterViewId = useSelector(Global.selectShipmentsFilteringActiveFilterViewId);
  const statusViewId = useSelectedShippingViewId();
  const enableNewEmailExperience = useBTEmailExperienceNew();
  const shouldShowVoidTypeColumn = useShouldShowVoidType();

  const columnConfiguration = useSelector((state) =>
    Global.selectShipmentsGridCurrentViewGridConfigOrDefault(state, {
      statusViewId,
      activeFilterViewId,
      enableNewEmailExperience,
    }),
  );
  const saveColumnConfiguration = useCallback(
    (columns) => {
      dispatch(saveUserSettingGridConfig('shipments', statusViewId, activeFilterViewId, columns));
      dispatch(shipmentGridChangeColumns(statusViewId, activeFilterViewId, columns));
    },
    [activeFilterViewId, dispatch, statusViewId],
  );
  let availableColumns = baseColumns;

  // Add refund assist columns if in refund assist view and sort alphabetically
  if (statusViewId === REFUND_ASSIST_VIEW_ID) {
    availableColumns = [...baseColumns, ...REFUND_ASSIST_COLUMNS].sort((a, b) => {
      const aMessage = a.getMessageDescriptor();
      const bMessage = b.getMessageDescriptor();
      return aMessage.defaultMessage.localeCompare(bMessage.defaultMessage);
    });
  }

  if (statusViewId === VOIDED_VIEW_ID && shouldShowVoidTypeColumn) {
    availableColumns = [...baseColumns, ...VOIDED_GRID_COLUMNS].sort((a, b) => {
      const aMessage = a.getMessageDescriptor();
      const bMessage = b.getMessageDescriptor();
      return aMessage.defaultMessage.localeCompare(bMessage.defaultMessage);
    });
  }

  const filteredBaseColumns = availableColumns.filter((column) => !EMAIL_COLUMN_IDS.includes(column.id));

  // Filter out void-type column from user config if they shouldn't see it
  const filteredColumnConfiguration = !shouldShowVoidTypeColumn
    ? columnConfiguration.filter((column) => !VOIDED_GRID_COLUMN_IDS.includes(column.id))
    : columnConfiguration;

  return (
    <EditColumnsModal
      baseColumns={enableNewEmailExperience ? filteredBaseColumns : availableColumns}
      columnConfiguration={filteredColumnConfiguration}
      defaultColumnConfigurations={defaultColumnConfigurations}
      onClose={onClose}
      saveColumnConfiguration={saveColumnConfiguration}
    />
  );
});
